import { Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { finalize, firstValueFrom } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { TokenStorageService } from '../../core/auth/token-storage.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FormRuntimeRendererComponent } from '../../shared/dynamic-form/form-runtime-renderer.component';
import { FormRuntimeAiOutcomeComponent } from '../../shared/dynamic-form/form-runtime-ai-outcome.component';
import { FormRuntimeProgressAdapter } from '../../shared/dynamic-form/form-runtime-progress.contracts';
import { FormAiOutcome, parseFormSchema } from '../../shared/dynamic-form/form-schema.models';
import {
  DynamicApiService,
  FormAiOutcomePayload,
  PublicFormRuntimeDescriptor,
  PublicFormRuntimeSubmissionRequest
} from '../dynamic/dynamic-api.service';
import { FormAnalyticsClient } from './form-analytics.client';
import { PublicFormCaptchaComponent } from './public-form-captcha.component';
import { parsePublicFormDisplayMode, PublicFormDisplayMode } from './public-form-url.util';
import { readLocalizedText } from '../../shared/dynamic-form/form-schema.models';

type PublicFormPageState =
  | 'loading'
  | 'ready'
  | 'success'
  | 'resultsGate'
  | 'aiOutcome'
  | 'aiError'
  | 'redirecting'
  | 'notFound'
  | 'closed'
  | 'error'
  | 'loginRequired'
  | 'loginForOutcome'
  | 'forbidden';

@Component({
  standalone: true,
  selector: 'ain-public-form-page',
  imports: [
    AinStateComponent,
    FormRuntimeRendererComponent,
    FormRuntimeAiOutcomeComponent,
    PublicFormCaptchaComponent,
    ButtonModule,
    ToastModule,
    RouterLink
  ],
  providers: [MessageService, FormAnalyticsClient],
  template: `
    <p-toast />
    <main
      class="public-form-page"
      [class.public-form-page--modal]="displayMode() === 'modal'"
      [class.public-form-page--inline]="displayMode() === 'inline'"
      [style.--public-form-primary]="primaryColor()">
      @switch (pageState()) {
        @case ('loading') {
          <ain-state type="loading" [title]="t('dynamic.public.loading')" />
        }
        @case ('notFound') {
          <ain-state type="empty" [title]="t('dynamic.public.notFoundTitle')" [message]="t('dynamic.public.notFoundMessage')" />
        }
        @case ('closed') {
          <ain-state type="empty" [title]="t('dynamic.public.closedTitle')" [message]="t('dynamic.public.closedMessage')" />
        }
        @case ('error') {
          <ain-state type="error" [title]="t('dynamic.public.loadFailedTitle')" [message]="t('dynamic.public.loadFailedMessage')" />
        }
        @case ('loginRequired') {
          <section class="auth-card">
            <ain-state type="empty" [title]="t('dynamic.public.loginRequiredTitle')" [message]="t('dynamic.public.loginRequiredMessage')" />
            @if (storefrontSignInUrl(); as signInUrl) {
              <a pButton [href]="signInUrl" target="_top" [label]="t('dynamic.public.loginAction')"></a>
            } @else {
              <a pButton routerLink="/login" [queryParams]="{ returnUrl: loginReturnUrl() }" [label]="t('dynamic.public.loginAction')"></a>
            }
          </section>
        }
        @case ('loginForOutcome') {
          <section class="success-card auth-card" role="status">
            <h1>{{ t('dynamic.public.successTitle') }}</h1>
            <p>{{ successMessage() }}</p>
            <ain-state type="empty" [title]="t('dynamic.public.loginForOutcomeTitle')" [message]="t('dynamic.public.loginForOutcomeMessage')" />
            @if (storefrontSignInUrl(); as signInUrl) {
              <a pButton [href]="signInUrl" target="_top" [label]="t('dynamic.public.loginForOutcomeAction')"></a>
            } @else {
              <a pButton routerLink="/login" [queryParams]="{ returnUrl: loginReturnUrl() }" [label]="t('dynamic.public.loginForOutcomeAction')"></a>
            }
          </section>
        }
        @case ('forbidden') {
          <ain-state type="empty" [title]="t('dynamic.public.forbiddenTitle')" [message]="t('dynamic.public.forbiddenMessage')" />
        }
        @case ('success') {
          <section class="success-card" role="status">
            <h1>{{ t('dynamic.public.successTitle') }}</h1>
            <p>{{ successMessage() }}</p>
            @if (pendingRedirectUrl()) {
              <button
                pButton
                type="button"
                class="redirect-action"
                [label]="t('dynamic.ai.outcome.continueRedirect')"
                (click)="onContinueRedirect()"></button>
            }
          </section>
        }
        @case ('resultsGate') {
          <section class="success-card auth-card" role="status">
            <h1>{{ t('dynamic.public.resultsGateTitle') }}</h1>
            <p>{{ t('dynamic.public.resultsGateMessage') }}</p>
            @if (showResultsLoginRequired()) {
              <ain-state type="empty" [title]="t('dynamic.public.loginForOutcomeTitle')" [message]="t('dynamic.public.loginForOutcomeMessage')" />
              @if (storefrontSignInUrl(); as signInUrl) {
                <a pButton [href]="signInUrl" target="_top" [label]="t('dynamic.public.loginForOutcomeAction')"></a>
              } @else {
                <a pButton routerLink="/login" [queryParams]="{ returnUrl: loginReturnUrl() }" [label]="t('dynamic.public.loginForOutcomeAction')"></a>
              }
            } @else {
              <button
                pButton
                type="button"
                [label]="t('dynamic.public.showResultsAction')"
                [loading]="showResultsBusy()"
                (click)="onShowResults()"></button>
            }
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('dynamic.public.retakeAction')"
              (click)="onRetake()"></button>
          </section>
        }
        @case ('aiOutcome') {
          <section class="success-card ai-outcome-card" role="status">
            <h1>{{ t('dynamic.public.successTitle') }}</h1>
            <p>{{ successMessage() }}</p>
            <ain-form-runtime-ai-outcome
              [outcome]="aiOutcomePayload()"
              [config]="aiOutcomeConfig()"
              [culture]="activeCulture()"
              [redirectUrl]="safeRedirectUrl()" />
          </section>
        }
        @case ('aiError') {
          <section class="success-card ai-outcome-card" role="status">
            <h1>{{ t('dynamic.public.successTitle') }}</h1>
            <p>{{ successMessage() }}</p>
            <ain-form-runtime-ai-outcome
              [outcome]="null"
              [config]="aiOutcomeConfig()"
              [culture]="activeCulture()"
              [errorKey]="'dynamic.ai.outcome.error'"
              (retry)="onAiOutcomeRetry()" />
          </section>
        }
        @case ('redirecting') {
          <section class="success-card" role="status">
            <h1>{{ t('dynamic.public.successTitle') }}</h1>
            <p>{{ successMessage() }}</p>
            <p class="redirect-hint">{{ t('dynamic.public.redirecting') }}</p>
          </section>
        }
        @case ('ready') {
          @if (descriptor(); as formDescriptor) {
            <section class="form-shell">
              @if (displayMode() !== 'inline') {
                <header class="form-header">
                  <h1>{{ formDescriptor.name }}</h1>
                </header>
              }

              @if (captchaEnabled()) {
                <p class="captcha-hint">{{ t('dynamic.public.captchaHint') }}</p>
                <ain-public-form-captcha
                  #captcha
                  [provider]="formDescriptor.captchaProvider"
                  [siteKey]="formDescriptor.captchaSiteKey"
                  (tokenChange)="captchaToken.set($event)" />
              }

              <ain-form-runtime-renderer
                [schemaJson]="schemaJson()"
                mode="runtime"
                [publicSlug]="formDescriptor.slug"
                [initialValues]="initialValues()"
                [culture]="activeCulture()"
                [saveResumeEnabled]="formDescriptor.saveResumeEnabled"
                [honeypotEnabled]="formDescriptor.honeypotEnabled"
                [progressAdapter]="progressAdapter()"
                [resumeToken]="resumeToken()"
                [resumeBaseUrl]="resumeBaseUrl()"
                [showRuntimeInfoHint]="displayMode() !== 'inline'"
                [showFormTitle]="displayMode() !== 'inline'"
                [showSubmitFooter]="true"
                [submitting]="submitBusy()"
                (submit)="onSubmit($event)"
                (fieldBlur)="onFieldBlur($event)" />
            </section>
          }
        }
      }
    </main>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      min-height: 100vh;
      background: linear-gradient(180deg, color-mix(in srgb, var(--public-form-primary, #0f766e) 8%, #f8fafc), #f8fafc);
    }

    :host:has(.public-form-page--inline) {
      min-height: 100%;
      background: #fff;
    }

    .public-form-page {
      min-height: 100vh;
      padding: 24px 16px 48px;
      box-sizing: border-box;
      width: 100%;
    }

    .public-form-page--modal {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .public-form-page--inline {
      min-height: 100%;
      padding: 20px 22px 28px;
      background: #fff;
      box-sizing: border-box;
    }

    .form-shell,
    .auth-card,
    .success-card {
      width: min(760px, 100%);
      margin: 0 auto;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
    }

    .public-form-page--inline .form-shell {
      width: 100%;
      max-width: none;
      margin: 0;
      border: 0;
      border-radius: 0;
      padding: 0;
      box-shadow: none;
      background: transparent;
    }

    .public-form-page--inline .auth-card,
    .public-form-page--inline .success-card {
      width: 100%;
      max-width: none;
      margin: 0;
      border: 0;
      box-shadow: none;
      padding: 8px 4px 16px;
    }

    .public-form-page--modal .form-shell,
    .public-form-page--modal .auth-card,
    .public-form-page--modal .success-card {
      width: min(640px, 100%);
    }

    .form-header h1 {
      margin: 0 0 16px;
      font-size: 1.5rem;
      color: #0f172a;
    }

    .auth-card {
      display: grid;
      gap: 16px;
      justify-items: start;
    }

    .success-card h1 {
      margin: 0 0 8px;
      color: #0f766e;
    }

    .success-card p {
      margin: 0;
      color: #475569;
    }

    .redirect-hint {
      margin-top: 12px !important;
      font-size: 0.875rem;
      color: #64748b;
    }

    .redirect-action {
      margin-top: 16px;
    }

    .ai-outcome-card {
      display: grid;
      gap: 16px;
    }

    .captcha-hint {
      margin: 0 0 8px;
      color: #64748b;
      font-size: 0.875rem;
    }
  `]
})
export class PublicFormPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly contextService = inject(CurrentContextService);
  private readonly analytics = inject(FormAnalyticsClient);
  private readonly destroyRef = inject(DestroyRef);

  private beforeUnloadHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private readonly captcha = viewChild<PublicFormCaptchaComponent>('captcha');
  private readonly runtimeRenderer = viewChild(FormRuntimeRendererComponent);

  readonly pageState = signal<PublicFormPageState>('loading');
  readonly descriptor = signal<PublicFormRuntimeDescriptor | null>(null);
  readonly progressAdapter = signal<FormRuntimeProgressAdapter | null>(null);
  readonly initialValues = signal<Record<string, unknown>>({});
  readonly resumeToken = signal<string | null>(null);
  readonly submitBusy = signal(false);
  readonly captchaToken = signal<string | null>(null);
  readonly successMessage = signal('');
  readonly aiOutcomePayload = signal<FormAiOutcomePayload | null>(null);
  readonly pendingRedirectUrl = signal<string | null>(null);
  readonly showResultsLoginRequired = signal(false);
  readonly showResultsBusy = signal(false);
  readonly displayMode = signal<PublicFormDisplayMode>('fullPage');
  readonly parentOrigin = signal<string | null>(null);
  readonly formSlug = signal('');

  readonly schemaJson = computed(() => {
    const descriptor = this.descriptor();
    if (!descriptor) {
      return null;
    }

    const base = descriptor.schemaJson?.trim();
    if (!base) {
      return null;
    }

    if (!descriptor.themeJson?.trim()) {
      return base;
    }

    try {
      const schema = JSON.parse(base) as Record<string, unknown>;
      const theme = JSON.parse(descriptor.themeJson) as Record<string, unknown>;
      return JSON.stringify({
        ...schema,
        theme: { ...(schema['theme'] as Record<string, unknown> | undefined), ...theme }
      });
    } catch {
      return base;
    }
  });

  readonly aiOutcomeConfig = computed<FormAiOutcome | null>(() => {
    const json = this.schemaJson();
    if (!json) {
      return null;
    }
    try {
      return parseFormSchema(json, this.activeCulture())?.aiOutcome ?? null;
    } catch {
      return null;
    }
  });

  readonly safeRedirectUrl = computed(() => {
    const url = this.pendingRedirectUrl();
    return url && this.isSafeRedirectUrl(url) ? url : null;
  });

  readonly primaryColor = computed(() => {
    const json = this.schemaJson();
    if (!json) {
      return '#0f766e';
    }

    try {
      const schema = JSON.parse(json) as { theme?: { primaryColor?: string } };
      return schema.theme?.primaryColor ?? '#0f766e';
    } catch {
      return '#0f766e';
    }
  });

  readonly captchaEnabled = computed(() => {
    const descriptor = this.descriptor();
    return descriptor?.captchaProvider === 'turnstile' && !!descriptor.captchaSiteKey;
  });

  readonly activeCulture = computed(() => this.localization.activeCulture());

  readonly loginReturnUrl = computed(() => {
    if (typeof window === 'undefined') {
      return '/';
    }
    return window.location.pathname + window.location.search;
  });

  readonly storefrontSignInUrl = computed(() => {
    const parent = this.parentOrigin();
    const slug = this.formSlug();
    if (!parent || !slug) {
      return null;
    }

    const returnUrl = `${parent}/forms/${encodeURIComponent(slug)}`;
    return `${parent}/sign-in?returnUrl=${encodeURIComponent(returnUrl)}`;
  });

  readonly resumeBaseUrl = computed(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('resume');
    return url.pathname.startsWith('/public/forms/resume/')
      ? `${url.origin}/public/forms/${this.descriptor()?.slug ?? ''}`
      : url.toString();
  });

  ngOnInit(): void {
    this.localization.loadResources().subscribe();
    this.displayMode.set(parsePublicFormDisplayMode(this.route.snapshot.queryParamMap.get('mode')));
    this.parentOrigin.set(parseValidParentOrigin(this.route.snapshot.queryParamMap.get('parentOrigin')));
    this.registerStorefrontAuthBridge();
    this.registerAnalyticsLifecycle();

    const resumeRouteToken = this.route.snapshot.paramMap.get('token');
    if (resumeRouteToken) {
      this.loadResume(resumeRouteToken);
      return;
    }

    const slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (!slug) {
      this.pageState.set('notFound');
      return;
    }

    this.formSlug.set(slug);
    this.loadBySlug(slug);
  }

  onSubmit(data: Record<string, unknown>): void {
    const descriptor = this.descriptor();
    const slug = descriptor?.slug;
    if (!descriptor || !slug) {
      return;
    }

    if (this.captchaEnabled() && !this.captchaToken()) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.public.captchaRequired') });
      return;
    }

    this.submitBusy.set(true);
    const request: PublicFormRuntimeSubmissionRequest = {
      data,
      anonymousCorrelationId: this.buildCorrelationId(),
      honeypot: descriptor.honeypotEnabled ? this.runtimeRenderer()?.getHoneypotValue() ?? null : null,
      captchaToken: this.captchaEnabled() ? this.captchaToken() : null,
      culture: this.activeCulture()
    };

    this.api.submitPublicForm(slug, request).pipe(
      finalize(() => this.submitBusy.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => {
        if (response.error === 'publication_closed') {
          this.pageState.set('closed');
          return;
        }

        if (response.error || response.issues?.length) {
          this.messages.add({ severity: 'error', summary: this.t('dynamic.public.submitFailed') });
          this.captcha()?.reset();
          return;
        }

        this.successMessage.set(this.resolveSuccessMessage(response.showMessage));
        this.analytics.markSubmitted();

        const redirectUrl = response.redirectUrl?.trim();
        const safeRedirect = redirectUrl && this.isSafeRedirectUrl(redirectUrl) ? redirectUrl : null;
        this.pendingRedirectUrl.set(safeRedirect);

        if (response.aiOutcome?.enabled) {
          if (!this.tokenStorage.getAccessToken()) {
            this.persistPendingClaim(slug, request.anonymousCorrelationId ?? '', response.submissionId);
          }
          this.showResultsLoginRequired.set(false);
          this.pageState.set('resultsGate');
          return;
        }

        if (safeRedirect) {
          this.pageState.set('success');
          return;
        }

        this.pageState.set('success');
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('dynamic.public.submitFailed') });
        this.captcha()?.reset();
      }
    });
  }

  onContinueRedirect(): void {
    const url = this.pendingRedirectUrl();
    if (url) {
      this.performSafeRedirect(url);
    }
  }

  onFieldBlur(event: { fieldKey: string; hasError: boolean }): void {
    if (event.hasError) {
      this.analytics.trackFieldError(event.fieldKey);
      return;
    }
    this.analytics.trackFieldInteraction(event.fieldKey);
  }

  onAiOutcomeRetry(): void {
    this.pageState.set('success');
  }

  onShowResults(): void {
    if (!this.tokenStorage.getAccessToken()) {
      this.showResultsLoginRequired.set(true);
      return;
    }

    const slug = this.descriptor()?.slug ?? this.formSlug();
    if (!slug) {
      return;
    }

    this.showResultsLoginRequired.set(false);
    this.showResultsBusy.set(true);
    this.api.getMyPublicFormResult(slug, this.activeCulture()).pipe(
      finalize(() => this.showResultsBusy.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: result => {
        if (!result.hasCompleted || !result.aiOutcome?.enabled) {
          this.pageState.set('aiError');
          return;
        }

        if (!result.aiOutcome.explanation?.trim()) {
          this.pageState.set('aiError');
          return;
        }

        this.successMessage.set(this.resolveSuccessMessage(result.showMessage));
        const redirectUrl = result.redirectUrl?.trim();
        const safeRedirect = redirectUrl && this.isSafeRedirectUrl(redirectUrl) ? redirectUrl : null;
        this.pendingRedirectUrl.set(safeRedirect);
        this.aiOutcomePayload.set(result.aiOutcome);
        this.pageState.set('aiOutcome');
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('dynamic.public.claimFailed') });
      }
    });
  }

  onRetake(): void {
    this.aiOutcomePayload.set(null);
    this.showResultsLoginRequired.set(false);
    this.initialValues.set({});
    this.pageState.set('ready');
    const slug = this.descriptor()?.slug;
    if (slug) {
      this.analytics.bind(slug);
      this.analytics.trackStart();
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveSuccessMessage(showMessage: Record<string, string> | string | null | undefined): string {
    const localized = readLocalizedText(showMessage, this.activeCulture());
    return localized || this.t('dynamic.public.successMessage');
  }

  private isSafeRedirectUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed) {
      return false;
    }

    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
      return false;
    }

    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return true;
    }

    try {
      const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : undefined);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private performSafeRedirect(url: string): void {
    if (typeof window === 'undefined' || !this.isSafeRedirectUrl(url)) {
      return;
    }

    if (url.startsWith('/') && !url.startsWith('//')) {
      window.location.assign(url);
      return;
    }

    window.location.assign(url);
  }

  private loadBySlug(slug: string): void {
    this.api.getPublicForm(slug).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: descriptor => {
        this.descriptor.set(descriptor);
        this.progressAdapter.set(this.api.createPublicProgressAdapter(slug));
        void this.evaluateAccess(descriptor).then(() => this.tryClaimPendingSubmission(slug));
      },
      error: error => {
        this.pageState.set(error.status === 404 ? 'notFound' : 'error');
      }
    });
  }

  private loadResume(token: string): void {
    this.resumeToken.set(token);
    this.api.resumePublicForm(token).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        if (response.error || !response.descriptor) {
          this.pageState.set(response.error === 'publication_not_found' ? 'notFound' : 'error');
          return;
        }

        this.descriptor.set(response.descriptor);
        this.formSlug.set(response.descriptor.slug);
        this.progressAdapter.set(this.api.createPublicProgressAdapter(response.descriptor.slug));
        try {
          this.initialValues.set(JSON.parse(response.progressDataJson || '{}') as Record<string, unknown>);
        } catch {
          this.initialValues.set({});
        }

        void this.evaluateAccess(response.descriptor);
      },
      error: () => this.pageState.set('error')
    });
  }

  private async evaluateAccess(descriptor: PublicFormRuntimeDescriptor): Promise<void> {
    if (descriptor.accessError) {
      switch (descriptor.accessError) {
        case 'authentication_required':
        case 'anonymous_not_allowed':
          this.pageState.set('loginRequired');
          return;
        case 'role_not_allowed':
          this.pageState.set('forbidden');
          return;
        default:
          this.pageState.set('forbidden');
          return;
      }
    }

    const token = this.tokenStorage.getAccessToken();
    if (descriptor.requiresAuthentication && !token) {
      this.pageState.set('loginRequired');
      return;
    }

    if (descriptor.allowedRoles?.length) {
      if (!token) {
        this.pageState.set('loginRequired');
        return;
      }

      try {
        const context = await firstValueFrom(this.contextService.loadContext());
        const roles = context?.roles ?? [];
        const allowed = descriptor.allowedRoles.some(role => roles.includes(role));
        if (!allowed) {
          this.pageState.set('forbidden');
          return;
        }
      } catch {
        this.pageState.set('forbidden');
        return;
      }
    }

    await this.tryEnterReadyOrResultsGate(descriptor);
  }

  private async tryEnterReadyOrResultsGate(descriptor: PublicFormRuntimeDescriptor): Promise<void> {
    const token = this.tokenStorage.getAccessToken();
    const aiEnabled = this.aiOutcomeConfig()?.enabled;

    if (token && aiEnabled && descriptor.slug) {
      try {
        const result = await firstValueFrom(
          this.api.getMyPublicFormResult(descriptor.slug, this.activeCulture())
        );
        if (result.hasCompleted) {
          this.successMessage.set(this.resolveSuccessMessage(result.showMessage));
          const redirectUrl = result.redirectUrl?.trim();
          const safeRedirect = redirectUrl && this.isSafeRedirectUrl(redirectUrl) ? redirectUrl : null;
          this.pendingRedirectUrl.set(safeRedirect);
          this.showResultsLoginRequired.set(false);
          this.pageState.set('resultsGate');
          return;
        }
      } catch {
        // Fall through to ready state when my-result is unavailable.
      }
    }

    this.enterReadyState(descriptor);
  }

  private enterReadyState(descriptor: PublicFormRuntimeDescriptor): void {
    this.pageState.set('ready');
    if (descriptor.slug) {
      this.analytics.bind(descriptor.slug);
      this.analytics.trackStart();
    }
  }

  private registerStorefrontAuthBridge(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const handler = (event: MessageEvent) => {
      const expectedOrigin = this.parentOrigin();
      if (!expectedOrigin || event.origin !== expectedOrigin) {
        return;
      }

      const data = event.data as { type?: string; token?: string | null } | null;
      if (!data || data.type !== 'ain.storefront.auth') {
        return;
      }

      const token = data.token;
      if (typeof token === 'string' && token.trim()) {
        this.tokenStorage.setAccessToken(token.trim());
        const slug = this.formSlug() || this.descriptor()?.slug;
        if (slug) {
          this.loadBySlug(slug);
        }
      }
    };

    window.addEventListener('message', handler);
    this.destroyRef.onDestroy(() => window.removeEventListener('message', handler));

    if (this.displayMode() === 'inline' && this.parentOrigin()) {
      window.parent.postMessage({ type: 'ain.storefront.auth.ready' }, this.parentOrigin()!);
    }
  }

  private registerAnalyticsLifecycle(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.beforeUnloadHandler = () => this.analytics.trackDropoff();
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.analytics.trackDropoff();
      }
    };

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.destroyRef.onDestroy(() => {
      this.analytics.trackDropoff();
      this.analytics.dispose();
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
    });
  }

  private buildCorrelationId(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const key = 'ain.publicFormCorrelationId';
    const existing = window.sessionStorage.getItem(key);
    if (existing) {
      return existing;
    }

    const created = crypto.randomUUID();
    window.sessionStorage.setItem(key, created);
    return created;
  }

  private persistPendingClaim(slug: string, anonymousCorrelationId: string, submissionId: string): void {
    if (typeof window === 'undefined' || !anonymousCorrelationId.trim()) {
      return;
    }

    window.sessionStorage.setItem('ain.publicFormPendingClaim.slug', slug);
    window.sessionStorage.setItem('ain.publicFormPendingClaim.anonymousCorrelationId', anonymousCorrelationId);
    window.sessionStorage.setItem('ain.publicFormPendingClaim.submissionId', submissionId);
  }

  private tryClaimPendingSubmission(slug: string): void {
    if (typeof window === 'undefined' || !this.tokenStorage.getAccessToken()) {
      return;
    }

    const pendingSlug = window.sessionStorage.getItem('ain.publicFormPendingClaim.slug');
    const correlationId = window.sessionStorage.getItem('ain.publicFormPendingClaim.anonymousCorrelationId');
    if (!pendingSlug || !correlationId || pendingSlug !== slug) {
      return;
    }

    this.api.claimPublicForm(slug, {
      anonymousCorrelationId: correlationId,
      culture: this.activeCulture()
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => this.applyClaimResponse(response),
      error: () => {
        this.messages.add({ severity: 'warn', summary: this.t('dynamic.public.claimFailed') });
      }
    });
  }

  private applyClaimResponse(response: {
    error?: string | null;
    showMessage?: Record<string, string> | string | null;
    redirectUrl?: string | null;
    aiOutcome?: FormAiOutcomePayload | null;
  }): void {
    if (response.error === 'already_claimed_by_other') {
      this.clearPendingClaim();
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.public.claimAlreadyOwned') });
      return;
    }

    if (response.error || !response.aiOutcome?.enabled) {
      if (response.error && response.error !== 'submission_not_found') {
        this.messages.add({ severity: 'warn', summary: this.t('dynamic.public.claimFailed') });
      }
      this.clearPendingClaim();
      return;
    }

    this.successMessage.set(this.resolveSuccessMessage(response.showMessage));

    const redirectUrl = response.redirectUrl?.trim();
    const safeRedirect = redirectUrl && this.isSafeRedirectUrl(redirectUrl) ? redirectUrl : null;
    this.pendingRedirectUrl.set(safeRedirect);
    this.showResultsLoginRequired.set(false);
    this.pageState.set('resultsGate');
    this.clearPendingClaim();
  }

  private clearPendingClaim(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem('ain.publicFormPendingClaim.slug');
    window.sessionStorage.removeItem('ain.publicFormPendingClaim.anonymousCorrelationId');
    window.sessionStorage.removeItem('ain.publicFormPendingClaim.submissionId');
  }
}

function parseValidParentOrigin(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    if (url.pathname !== '/' && url.pathname !== '') {
      return null;
    }

    if (url.search || url.hash) {
      return null;
    }

    const canonical = url.origin;
    const normalized = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
    return normalized === canonical ? canonical : null;
  } catch {
    return null;
  }
}
